import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'motion/react';
import { ArrowLeft, Clock, Eye, ArrowRight, Star, Linkedin, BookOpen, Award } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/layout/Layout';
import LazyImage from '../components/ui/LazyImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 60 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
};

const BlogCard = ({ post, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });
  const imageUrl = post.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80';
  const readTime = post.read_time || 5;
  const categoryDisplay = post.category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Blog';

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }} transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}>
      <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}>
        <motion.article className="group card-fresh overflow-hidden" whileHover={{ y: -8 }}>
          <div className="relative h-48 -mx-8 -mt-8 mb-6 overflow-hidden">
            <LazyImage src={post.featured_image} alt={post.title} className="w-full h-full group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <Link to={`/blog/category/${post.category}`} onClick={e => e.stopPropagation()}>
                <span className="px-3 py-1 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white text-xs font-semibold rounded-full">{categoryDisplay}</span>
              </Link>
              {post.is_featured && (
                <span className="px-2 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1"><Star size={10} fill="currentColor" /> Featured</span>
              )}
            </div>
          </div>
          <div className="relative z-10">
            <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-3 group-hover:text-[#FF6B35] transition-colors line-clamp-2">{post.title}</h3>
            {post.excerpt && <p className="text-[#5A5A5A] text-sm mb-4 line-clamp-2">{post.excerpt}</p>}
            <div className="flex items-center gap-4 text-[#8A8A8A] text-sm mb-4">
              <span className="flex items-center gap-1"><Clock size={14} />{readTime} min</span>
              <span className="flex items-center gap-1"><Eye size={14} />{post.views || 0}</span>
            </div>
            <div className="flex items-center justify-end">
              <ArrowRight size={18} className="text-[#8A8A8A] group-hover:text-[#FF6B35] group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </motion.article>
      </Link>
    </motion.div>
  );
};

export default function BlogAuthor() {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/public/blog/authors/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setAuthor(data.author);
          setPosts(data.posts || []);
          setTotal(data.total || 0);
          setSchema(data.schema);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
          <motion.div className="w-12 h-12 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
        </div>
      </Layout>
    );
  }

  if (!author) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#FFFBF5] flex flex-col items-center justify-center">
          <h1 className="font-display text-4xl text-[#2D2D2D] mb-4">Author Not Found</h1>
          <p className="text-[#5A5A5A] mb-8">The author profile you're looking for doesn't exist.</p>
          <Link to="/blog" className="btn-sunrise"><span className="flex items-center gap-2"><ArrowLeft size={18} />Back to Blog</span></Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{author.name} | Buzz Craft Maven Blog</title>
        <meta name="description" content={author.bio || `Read articles by ${author.name} on Buzz Craft Maven.`} />
        {schema && (
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        )}
      </Helmet>

      {/* Hero - Author Profile */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-sunrise-hero relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div className="absolute top-20 right-[15%] w-[400px] h-[400px] sun-orb sun-orb-primary" animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity }} />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#5A5A5A] hover:text-[#FF6B35] transition-colors mb-10" data-testid="back-to-blog">
            <ArrowLeft size={18} /> Back to Blog
          </Link>

          <div className="flex flex-col md:flex-row items-start gap-8">
            {/* Avatar */}
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
              {author.profile_image ? (
                <img src={author.profile_image} alt={author.name} className="w-28 h-28 md:w-36 md:h-36 rounded-2xl object-cover border-4 border-white shadow-xl" />
              ) : (
                <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center text-white font-display text-5xl md:text-6xl font-bold shadow-xl" data-testid="author-avatar">
                  {author.name?.charAt(0)}
                </div>
              )}
            </motion.div>

            {/* Info */}
            <div className="flex-1">
              <TextReveal>
                <h1 className="font-display text-4xl md:text-5xl text-[#2D2D2D] leading-tight mb-3" data-testid="author-name">{author.name}</h1>
              </TextReveal>

              {author.expertise && author.expertise.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex flex-wrap gap-2 mb-4">
                  {author.expertise.map((skill, i) => (
                    <span key={i} className="px-3 py-1 bg-[#FF6B35]/10 text-[#FF6B35] text-sm rounded-full font-medium">{skill}</span>
                  ))}
                </motion.div>
              )}

              {author.bio && (
                <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-[#5A5A5A] text-lg max-w-2xl leading-relaxed mb-4" data-testid="author-bio">
                  {author.bio}
                </motion.p>
              )}

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-[#5A5A5A]">
                  <BookOpen size={18} className="text-[#FF6B35]" />
                  <span className="font-semibold text-[#2D2D2D]">{total}</span> {total === 1 ? 'Article' : 'Articles'}
                </div>
                {author.expertise && (
                  <div className="flex items-center gap-2 text-[#5A5A5A]">
                    <Award size={18} className="text-[#FF6B35]" />
                    <span>{author.expertise.length} Specialties</span>
                  </div>
                )}
                {author.linkedin_url && (
                  <a href={author.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#0077B5] hover:opacity-80 transition-opacity" data-testid="author-linkedin">
                    <Linkedin size={18} /> LinkedIn
                  </a>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Author's Articles */}
      <section className="section-padding bg-[#FFFBF5]">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal>
            <h2 className="font-display text-2xl md:text-3xl text-[#2D2D2D] mb-8">
              Articles by <span className="text-gradient-sunrise">{author.name}</span>
            </h2>
          </TextReveal>

          {posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="author-posts-grid">
              {posts.map((post, index) => (
                <BlogCard key={post.id} post={post} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-[#5A5A5A] text-lg">No published articles yet.</p>
              <Link to="/blog" className="inline-flex items-center gap-2 mt-6 text-[#FF6B35] hover:underline"><ArrowLeft size={16} /> Browse all articles</Link>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
